import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  PenTool,
  ShieldAlert,
} from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { formatDate } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface WorkspacePage {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  pageType: string;
  sitePath: string | null;
  publishedUrl: string | null;
  updatedAt: string;
}

interface WorkspaceSite {
  id: string;
  name: string;
  slug: string;
  status: string;
  reviewState: "live" | "ready_for_review" | "changes_pending_review" | "empty";
  health: "healthy" | "attention" | "critical";
  primaryPublishedUrl: string | null;
  updatedAt: string;
  healthNotes: string[];
  counts: {
    pageCount: number;
    publishedPageCount: number;
    draftPageCount: number;
    archivedPageCount: number;
  };
  pages: WorkspacePage[];
}

interface WorkspaceTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  complianceTier: string;
  health: "healthy" | "attention" | "critical";
  reviewState: "live" | "ready_for_review" | "changes_pending_review" | "empty";
  healthNotes: string[];
  loadError: string | null;
  counts: {
    siteCount: number;
    pageCount: number;
    publishedPageCount: number;
    draftPageCount: number;
    standalonePageCount: number;
  };
  sites: WorkspaceSite[];
  standalonePages: WorkspacePage[];
}

interface WorkspaceResponse {
  data: {
    tenants: WorkspaceTenant[];
  };
}

const HEALTH_TONE: Record<WorkspaceTenant["health"], string> = {
  healthy: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  attention: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  critical: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
};

const REVIEW_LABEL: Record<WorkspaceTenant["reviewState"], string> = {
  live: "Live",
  ready_for_review: "Ready for review",
  changes_pending_review: "Pending review",
  empty: "Setup needed",
};

export default function AgencyManagePage() {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const { session } = useAuth();
  const orgId = session?.activeOrganizationId ?? null;
  const [tenant, setTenant] = useState<WorkspaceTenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !clientId) {
      setTenant(null);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    async function loadTenant() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiFetch<WorkspaceResponse>(`/platform/agency/builder-workspace/${orgId}`);
        const matchedTenant = response.data.tenants.find((item) => item.id === clientId) ?? null;

        if (isCancelled) {
          return;
        }

        if (!matchedTenant) {
          setTenant(null);
          setError("The selected client is not part of the active organization workspace.");
          return;
        }

        setTenant(matchedTenant);
      } catch (loadError) {
        if (!isCancelled) {
          setTenant(null);
          setError(loadError instanceof ApiError ? loadError.message : "Failed to load the managed client workspace.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadTenant();

    return () => {
      isCancelled = true;
    };
  }, [clientId, orgId]);

  const openLiveCount = useMemo(() => {
    return tenant?.sites.filter((site) => site.primaryPublishedUrl).length ?? 0;
  }, [tenant]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-44" />
        <div className="grid gap-4 lg:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <SkeletonCard key={item} className="h-36 rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <EmptyState
        title="Managed workspace unavailable"
        description={error ?? "This client workspace could not be resolved from the current organization."}
      />
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(245,158,11,0.12),_transparent_40%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <Link
              to="/agency"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to agency workspace
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">{tenant.name}</h1>
              <StatusChip tone={tenant.health}>{tenant.health}</StatusChip>
              <StatusChip tone={tenant.reviewState === "live" ? "healthy" : tenant.reviewState === "empty" ? "critical" : "attention"}>
                {REVIEW_LABEL[tenant.reviewState]}
              </StatusChip>
            </div>
            <p className="text-sm leading-6 text-muted-foreground">
              {tenant.slug} · {tenant.plan} · {tenant.complianceTier}. This managed view reuses the client&apos;s real site and page records and keeps cross-tenant edits blocked until you switch into an audited impersonation session.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/impersonation")}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              <PenTool className="h-4 w-4" />
              Start impersonation to edit
            </button>
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-border px-4 py-2 text-sm font-medium text-muted-foreground"
            >
              <ShieldAlert className="h-4 w-4" />
              Direct publish blocked
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Sites" value={tenant.counts.siteCount} icon={Globe} />
        <MetricCard label="Pages" value={tenant.counts.pageCount} icon={FileText} />
        <MetricCard label="Live Sites" value={openLiveCount} icon={ExternalLink} />
        <MetricCard label="Draft Pages" value={tenant.counts.draftPageCount} icon={AlertTriangle} />
      </section>

      {tenant.loadError ? (
        <div className={`rounded-3xl border px-5 py-4 text-sm ${HEALTH_TONE.critical}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <p>{tenant.loadError}</p>
          </div>
        </div>
      ) : null}

      {tenant.healthNotes.length > 0 ? (
        <div className={`rounded-3xl border px-5 py-4 text-sm ${HEALTH_TONE[tenant.health]}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div className="space-y-1">
              {tenant.healthNotes.map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <section className="space-y-4">
        {tenant.sites.length === 0 ? (
          <EmptyState
            title="No client sites found"
            description="This tenant does not have any site records yet, so there is nothing to review from the agency workspace."
          />
        ) : (
          tenant.sites.map((site) => (
            <article key={site.id} className="stagger-item rounded-[28px] border border-border bg-card p-6">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold text-foreground">{site.name}</h2>
                    <StatusChip tone={site.health}>{site.health}</StatusChip>
                    <StatusChip tone={site.reviewState === "live" ? "healthy" : site.reviewState === "empty" ? "critical" : "attention"}>
                      {REVIEW_LABEL[site.reviewState]}
                    </StatusChip>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    /{site.slug} · {site.counts.pageCount} pages · {site.counts.publishedPageCount} live · {site.counts.draftPageCount} drafts
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/impersonation")}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                  >
                    Use impersonation to edit
                  </button>
                  {site.primaryPublishedUrl ? (
                    <a
                      href={site.primaryPublishedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      View live
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              </div>

              {site.healthNotes.length > 0 ? (
                <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${HEALTH_TONE[site.health]}`}>
                  {site.healthNotes.map((note) => (
                    <p key={note}>{note}</p>
                  ))}
                </div>
              ) : null}

              <div className="mt-5 overflow-hidden rounded-3xl border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Page</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Path</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last updated</th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {site.pages.map((page) => (
                      <tr key={page.id} className="stagger-item">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-foreground">{page.title}</p>
                            <p className="text-xs text-muted-foreground">{page.slug}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusChip tone={page.status === "published" ? "healthy" : page.status === "draft" ? "attention" : "critical"}>
                            {page.status}
                          </StatusChip>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{page.sitePath ?? "Standalone"}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDate(page.updatedAt, "en-GB")}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {page.publishedUrl ? (
                              <a
                                href={page.publishedUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
                              >
                                View
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            ) : (
                              <span className="rounded-full border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground">
                                No live URL
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          ))
        )}
      </section>

      {tenant.standalonePages.length > 0 ? (
        <section className="rounded-[28px] border border-border bg-card p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-foreground">Standalone pages</h2>
            <p className="text-sm text-muted-foreground">
              These pages are not attached to a site yet. They stay visible here so the agency can see unfinished builder work without fabricating site assignments.
            </p>
          </div>

          <div className="space-y-3">
            {tenant.standalonePages.map((page) => (
              <div key={page.id} className="stagger-item flex flex-col gap-3 rounded-2xl border border-border bg-background px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-medium text-foreground">{page.title}</p>
                  <p className="text-sm text-muted-foreground">{page.slug} · {page.pageType}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusChip tone={page.status === "published" ? "healthy" : page.status === "draft" ? "attention" : "critical"}>
                    {page.status}
                  </StatusChip>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/impersonation")}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
                  >
                    Edit via impersonation
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof Globe;
}) {
  return (
    <article className="stagger-item rounded-[28px] border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-foreground">{value}</p>
    </article>
  );
}

function StatusChip({
  tone,
  children,
}: {
  tone: "healthy" | "attention" | "critical";
  children: string;
}) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${HEALTH_TONE[tone]}`}>
      {children}
    </span>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-[28px] border border-dashed border-border bg-card p-10 text-center">
      <div className="max-w-lg space-y-3">
        <Loader2 className="mx-auto h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
